<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useDataVisible } from '../composables/useDataVisible'
import type { ChartType, ReportViewType } from '../models/chart.types'
import type {
  DataVisibleCellSlotProps,
  DataVisibleControlsSlotProps,
  DataVisibleProps,
} from '../models/dataVisible.types'
import type { ReportFilter } from '../models/filter.types'
import type { ReportTableSortV1 } from '../models/tableConfig.types'
import type { ReportTableType } from '../models/tablePresentation.types'
import type { TrendTableFrequency } from '../models/trendTable.types'
import ReportTableCustomizePanel from './controls/ReportTableCustomizePanel.vue'
import DataVisibleContent from './DataVisibleContent.vue'
import DataVisibleToolbar from './DataVisibleToolbar.vue'
import { useAddToDashboard, type DashboardWidgetWire } from '@/features/dashboards'
import { useI18n } from '@/shared/i18n'
import type { ChartMenuItem } from '@/shared/ui/chart'

const props = withDefaults(defineProps<DataVisibleProps>(), {
  defaultPostData: '',
  filters: () => [],
  userFilters: () => [],
  sortOrder: -1,
  view: 'table',
  composition: 'single',
  chartType: 'column',
  tableType: 'table',
  tableMode: 'standard',
  pagination: () => ({}),
  breakdown: null,
  matrixDimension: null,
  pivotMetric: null,
  heatmap: false,
  namedViews: () => [],
  cardMediaType: 'Image',
  readOnly: false,
  tableModes: true,
  showTotal: true,
})
const emit = defineEmits<{
  'update:view': [value: ReportViewType]
  'update:chartType': [value: ChartType]
  'update:sort': [value: ReportTableSortV1 | null]
  'update:columnOrder': [value: string[]]
  'update:columnWidths': [value: Record<string, number>]
  'update:userFilters': [value: ReportFilter[]]
  'update:tableType': [value: ReportTableType]
  'update:breakdown': [value: string | null]
  'update:frequency': [value: TrendTableFrequency]
}>()
defineSlots<{
  controls?: (props: DataVisibleControlsSlotProps) => unknown
  cell?: (props: DataVisibleCellSlotProps) => unknown
  cohort?: () => unknown
}>()

const surface = ref<HTMLElement | null>(null)
const content = ref<HTMLElement | HTMLElement[] | null>(null)
const contentHeight = ref<number | null>(null)
const chartHidden = ref(false)
let contentObserver: ResizeObserver | null = null
const { t } = useI18n()

watch(
  () => {
    const collected = content.value
    return (Array.isArray(collected) ? (collected.at(-1) ?? null) : collected) as HTMLElement | null
  },
  (element) => {
    contentObserver?.disconnect()
    contentObserver = null
    contentHeight.value = null
    if (!element || typeof ResizeObserver === 'undefined') return
    const updateHeight = (): void => {
      const height = Math.ceil(element.getBoundingClientRect().height)
      if (height > 0) contentHeight.value = height
    }
    updateHeight()
    contentObserver = new ResizeObserver(updateHeight)
    contentObserver.observe(element)
  },
  { flush: 'post' },
)
onBeforeUnmount(() => contentObserver?.disconnect())
const {
  chartTable,
  fullscreen,
  chart,
  chartContract,
  report,
  request,
  exportFlow,
  state,
  refreshing,
  renderValue,
  table,
  presentation,
  presentedColumns,
  availableTableTypes,
  effectiveTableType,
  matrixAssignment,
  tableUnavailable,
  savedFilters,
  dropdownables,
  loadDropdownValues,
  savedViews,
  savedViewId,
  filterDraft,
  activeUserFilters,
  cardStatus,
  customizeOpen,
  expandableFields,
  activeExpandedFields,
  applyCustomization,
  updateChartType,
  updateTableType,
  updateBreakdown,
  updateFrequency,
  applyFilters,
  saveFilter,
  removeFilter,
  saveNamedView,
  selectNamedView,
  renameNamedView,
  removeNamedView,
  updateTableSort,
  updateColumnOrder,
  updateColumnWidths,
  updateFrozenField,
  updateExpandedField,
} = useDataVisible(props, emit, surface)

interface DataVisibleSurface {
  key: string
  toolbarView: ReportViewType
  contentView: ReportViewType
}

function visibleSurfaces(): DataVisibleSurface[] {
  if (chartTable.value) {
    if (chartHidden.value) return [{ key: 'table', toolbarView: 'table', contentView: 'table' }]
    return [
      { key: 'chart', toolbarView: 'chart', contentView: 'chart' },
      { key: 'table', toolbarView: 'table', contentView: 'table' },
    ]
  }

  return [
    {
      key: 'single',
      toolbarView: chartHidden.value ? 'table' : chart.view.value,
      contentView: chartHidden.value
        ? 'table'
        : chart.view.value === 'cohort'
          ? 'cohort'
          : renderValue.value.view,
    },
  ]
}

const surfaces = computed<DataVisibleSurface[]>(visibleSurfaces)
const addToDashboard = useAddToDashboard()
const chartMenuItems = computed<ChartMenuItem[]>(() => {
  if (!chartTable.value && !fullscreen.isFullscreen.value) return []
  return [{ id: 'hideChart', text: t('reports.actions.hideChart'), onclick: hideChart }]
})

function widgetKind(view: ReportViewType): string {
  if (chartTable.value) return 'double'
  if (view === 'table' && effectiveTableType.value === 'matrix_table') return 'matrix'
  return view
}

function openAddToDashboard(view: ReportViewType): void {
  const widget: DashboardWidgetWire = {
    id: props.id,
    name: props.ariaLabel,
    visibleType: widgetKind(view),
    reportId: props.reportId,
    ...(props.partitionName ? { partition: props.partitionName } : {}),
    ...(props.partitionOnly ? { partition_only: true } : {}),
    ...(props.workspaceIds?.length ? { accountWorkspaces: [...props.workspaceIds] } : {}),
    setDimensions: [...props.dimensions],
    setMetrics: [...props.metrics],
    chartDimensions: [...(props.chartDimensions ?? props.dimensions)],
    chartMetrics: [...(props.chartMetrics ?? props.metrics)],
    defaultChart: chart.chartType.value,
    fieldValues: { ...chart.fieldSelection.value },
    defaultFilters: [...activeUserFilters.value],
    defaultBreakdown: presentation.breakdownField.value,
    sortField: table.tableSort.value?.field ?? null,
    selectedTable: effectiveTableType.value === 'matrix_table' ? 'Matrix' : 'Table',
    defaultPostData: props.defaultPostData,
  }
  addToDashboard.open({ widgets: [widget], title: props.ariaLabel })
}

function updateView(value: ReportViewType): void {
  if (value === 'chart') chartHidden.value = false
  chart.setView(value)
}

function hideChart(): void {
  chartHidden.value = true
  if (!chartTable.value) chart.setView('table')
}
function showChart(): void {
  chartHidden.value = false
  if (!chartTable.value) chart.setView('chart')
}
</script>

<template>
  <section
    ref="surface"
    class="data-visible grid w-full max-w-full min-w-0 p-2 mb-8"
    :class="{
      'data-visible--fullscreen': fullscreen.isFallback.value,
      'fixed inset-0 z-overlay-base overflow-auto bg-surface p-lg': fullscreen.isFallback.value,
      'overflow-hidden': !fullscreen.isFallback.value,
      'data-visible--chart-table': chartTable,
      'gap-[var(--ui-report-chart-table-gap)]': chartTable,
      'gap-sm': !chartTable,
    }"
    :data-report-view-id="id"
    :aria-busy="report.state.value === 'loading'"
  >
    <template v-for="reportSurface in surfaces" :key="reportSurface.key">
      <div
        class="data-visible__surface grid min-w-0 gap-sm"
        :data-report-surface="reportSurface.contentView"
      >
        <DataVisibleToolbar
          :view="reportSurface.toolbarView"
          :recipe="
            chartTable
              ? reportSurface.toolbarView === 'chart'
                ? 'chart-table-chart'
                : 'chart-table-table'
              : 'single'
          "
          :chart-type="chart.chartType.value"
          :views="chartTable ? [reportSurface.toolbarView] : chart.configuredViews.value"
          :chart-types="chart.availableChartTypes.value"
          :chart-roles="chart.activeMeta.value?.fieldRoles ?? []"
          :chart-field-sources="chart.fieldSources.value"
          :chart-field-selection="chart.fieldSelection.value"
          :chart-fields-disabled="chartContract.validating.value"
          :columns="presentedColumns"
          :table-modes="tableModes"
          :table-types="availableTableTypes"
          :table-type="effectiveTableType"
          :breakdown="presentation.breakdownField.value"
          :frequency="presentation.frequency.value"
          :saved-view-id="savedViewId"
          :saved-views="savedViews"
          :filters="filterDraft"
          :saved-filters="savedFilters.savedFilters.value"
          :dropdownables="dropdownables"
          :on-load-dropdown-values="loadDropdownValues"
          :read-only="readOnly"
          :state="state"
          :table-config="table.config.value"
          :sort="table.tableSort.value"
          :card-status="cardStatus"
          :export-disabled="!exportFlow.canExport.value"
          :export-busy="exportFlow.busy.value"
          :share-picker="exportFlow.sharePicker"
          :fullscreen="fullscreen.isFullscreen.value"
          :allow-hide-chart="
            reportSurface.toolbarView === 'chart' && (chartTable || fullscreen.isFullscreen.value)
          "
          :allow-show-chart="reportSurface.toolbarView === 'table' && chartHidden"
          :customize-open="customizeOpen"
          :maintenance-schedule-id="maintenanceScheduleId"
          @view="updateView"
          @chart-type="updateChartType"
          @chart-fields="chart.setFieldSelection"
          @table-type="updateTableType"
          @breakdown="updateBreakdown"
          @frequency="updateFrequency"
          @sort="updateTableSort"
          @card-status="cardStatus = $event"
          @saved-view="selectNamedView"
          @rename-saved-view="renameNamedView"
          @remove-saved-view="removeNamedView"
          @filter-draft="filterDraft = $event"
          @apply-filters="applyFilters"
          @save-filter="saveFilter"
          @remove-filter="removeFilter"
          @customize="customizeOpen = !customizeOpen"
          @export-sheet="exportFlow.exportSheet"
          @download-excel="exportFlow.downloadExcel"
          @toggle-fullscreen="fullscreen.toggle"
          @hide-chart="hideChart"
          @show-chart="showChart"
          @add-to-dashboard="openAddToDashboard"
        >
          <template
            v-if="$slots.controls && (!chartTable || reportSurface.toolbarView === 'table')"
            #controls="slotProps"
          >
            <slot name="controls" v-bind="slotProps" />
          </template>
        </DataVisibleToolbar>

        <div class="data-visible__body flex flex-wrap items-start gap-sm">
          <div ref="content" class="data-visible__content w-0 max-w-full min-w-0 flex-[1_1_20rem]">
            <DataVisibleContent
              :view="reportSurface.contentView"
              :view-unavailable="renderValue.viewUnavailable"
              :table-unavailable="tableUnavailable || request.trendUnavailable.value"
              :chart-catalog-loading="renderValue.chartCatalogLoading"
              :chart-type="chart.chartType.value"
              :chart-unavailable="renderValue.chartUnavailable"
              :chart-contract-valid="renderValue.chartContractValid"
              :state="state"
              :refreshing="refreshing"
              :rows="renderValue.rows"
              :columns="renderValue.columns"
              :table-config="table.config.value"
              :table-sort="table.tableSort.value"
              :table-type="effectiveTableType"
              :enable-freeze="!readOnly"
              :breakdown-field="presentation.breakdownField.value"
              :matrix-dimension="matrixAssignment?.matrixDimension ?? null"
              :pivot-metric="matrixAssignment?.pivotMetric ?? null"
              :total="showTotal ? renderValue.total : null"
              :other-total="showTotal ? renderValue.otherTotal : null"
              :grand-total="showTotal ? renderValue.grandTotal : null"
              :ga4-total="showTotal ? renderValue.ga4Total : null"
              :total-row-kinds="presentation.totalRows.value"
              :heatmap="heatmap"
              :comparison-expandable-fields="readOnly ? [] : expandableFields"
              :comparison-expanded-fields="activeExpandedFields"
              :trend="reportSurface.contentView === 'table' ? renderValue.trend : null"
              :pagination="
                reportSurface.contentView === 'table' || reportSurface.contentView === 'card'
                  ? renderValue.pagination
                  : null
              "
              :card-media-type="cardMediaType"
              :card-dimension-fields="cardDimensions ?? []"
              :card-metric-fields="cardMetrics ?? []"
              :chart-spec="renderValue.chartSpec"
              :chart-menu-items="
                reportSurface.contentView === 'chart' && !chartHidden ? chartMenuItems : []
              "
              :channel="channel ?? ''"
              v-bind="{ ariaLabel, emptyLabel, errorLabel, retryLabel, filterable: !readOnly }"
              @reload="report.reload"
              @sort="updateTableSort"
              @reorder="updateColumnOrder"
              @resize="updateColumnWidths"
              @freeze="updateFrozenField"
              @comparison-expand="updateExpandedField"
              @load-more="request.loadMore"
              @add-filter="applyFilters([...activeUserFilters, $event])"
            >
              <template v-if="$slots.cohort" #cohort>
                <slot name="cohort" />
              </template>
              <template v-if="$slots.cell" #cell="slotProps">
                <slot name="cell" v-bind="slotProps" />
              </template>
            </DataVisibleContent>
          </div>

          <ReportTableCustomizePanel
            v-if="!readOnly && (!chartTable || reportSurface.toolbarView === 'table')"
            v-model="customizeOpen"
            class="data-visible__rail max-w-full flex-[0_0_20rem] self-start max-md:basis-full"
            :style="
              contentHeight === null
                ? undefined
                : { height: `${contentHeight}px`, maxHeight: `${contentHeight}px` }
            "
            :columns="table.columns.value"
            :visible-fields="presentation.visibleFields.value"
            :frozen-fields="presentation.frozenFields.value"
            :expanded-fields="presentation.expandedFields.value"
            :expandable-fields="expandableFields"
            :required-fields="table.config.value.requiredFields"
            :table-type="effectiveTableType"
            :breakdown-field="presentation.breakdownField.value"
            :total-rows="presentation.totalRows.value"
            :ga4-total-available="Boolean(renderValue.ga4Total)"
            :filters="activeUserFilters"
            @apply="applyCustomization"
            @save-view="saveNamedView"
          />
        </div>
      </div>
    </template>
  </section>
</template>
<style scoped>
.data-visible .data-visible__content :deep(.ui-data-table) {
  max-width: 100%;
  overscroll-behavior-x: contain;
}
</style>
